import { FormikProps } from 'formik'
import {
  BulkCreateAttendanceTimeBlockCompensationMutation,
  BulkCreateTimeBlockSupplementAttendanceManualCompensationTimeBlocksInput,
  PayrollPolicyPeriodTypesPeriodTypeEnum,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import { useBulkCreateAttendanceTimeBlockCompensation } from 'modules/attendance/hooks/useBulkCreateAttendanceTimeBlockCompensation'
import { UniqueTimeBlockData } from 'modules/attendance/store/models/UniqueTimeBlockData'

export type PayrollPeriod = 'current' | 'next'

export interface TimeBlock {
  source_id: number
  employee_id: number
  minutes_in_cents: number
  raw_minutes_in_cents: number
  equivalent_minutes_in_cents: number
  contracts_taxonomy_id: number
}

export interface FormData {
  employeeId: number
  period: PayrollPolicyPeriodTypesPeriodTypeEnum
  startOn: string
  endOn: string
  timeBlocks: UniqueTimeBlockData[]
}

const TimeBlocksSupplementForm = ({
  setResponseData,
  employeeId,
  startOn,
  endOn,
  compensationBlockIds,
}: {
  setResponseData: (
    result:
      | BulkCreateAttendanceTimeBlockCompensationMutation['attendanceMutations']['bulkCreateTimeBlockSupplementAttendanceManualCompensation']['manualCompensation']
      | undefined
      | null
  ) => void
  employeeId: number
  startOn: string
  endOn: string
  compensationBlockIds: string[]
}) => {
  const { create } = useBulkCreateAttendanceTimeBlockCompensation()

  const defaultValues: FormData = {
    employeeId: employeeId,
    period: 'current',
    startOn: startOn,
    endOn: endOn,
    timeBlocks: [],
  }

  const form: FormikProps<FormData> = useForm({
    defaultValues: defaultValues,
    onSubmit: async (data, { setErrors }) => {
      const formattedTimeBlocks: BulkCreateTimeBlockSupplementAttendanceManualCompensationTimeBlocksInput[] =
        data.timeBlocks.map((timeBlock) => {
          return {
            employeeId: data.employeeId,
            equivalentMinutesInCents: timeBlock.equivalentMinutes * 100,
            minutesInCents: timeBlock.minutes * 100,
            rawMinutesInCents: timeBlock.rawMinutes * 100,
            sourceId: timeBlock.timeSettingsCustomTimeRangeCategoryId,
          }
        })

      const usingCompensationBlockIds = compensationBlockIds.length > 0

      const payload = {
        ...defaultValues,
        employeeId: data.employeeId,
        period: data.period,
        compensationBlockIds: usingCompensationBlockIds ? compensationBlockIds : null,
        timeBlocks: usingCompensationBlockIds ? null : formattedTimeBlocks,
      }

      const result = await create(payload)

      const handled = handleResult(result, ({ timeBlocks }) => {
        setErrors({ timeBlocks })
      })

      form.resetForm()

      if (handled.success) {
        setResponseData(handled.data.manualCompensation)
        dispatch({
          type: EventTypes.ATTENDANCE_SHIFT_SAVED,
        })
      } else {
        const error = handled.errors[0]

        const message =
          error && 'messages' in error
            ? error.messages[0] || i18n.t('clock_in.sidepanel.compensation.period_not_compensated')
            : i18n.t('clock_in.sidepanel.compensation.period_not_compensated')

        app.setFlash({
          type: 'wrong',
          message,
          time: 2000,
        })
      }

      return handled
    },
  })

  return form
}

export default TimeBlocksSupplementForm
